import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout, Typography, Button, Avatar, Dropdown,
  Breadcrumb, Divider, Table, Space, Empty, Spin, Drawer, Alert, Tooltip,
} from 'antd';
import {
  LogoutOutlined, LockOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined,
  FileAddOutlined, UnorderedListOutlined, DashboardOutlined,
  AuditOutlined, NumberOutlined, BookOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import InvoiceSubmissionForm from './InvoiceSubmissionForm';
import SubmissionsList from './SubmissionsList';
import SubmissionDetail from './SubmissionDetail';
import BatchManagementView from './BatchManagementView';
import LogSheetView from './LogSheetView';
import RevisionUploadForm from './RevisionUploadForm';
import ProcurementHowToModal from './ProcurementHowToModal';
import RejectedInvoicesReminder from './RejectedInvoicesReminder';
import ChangePasswordModal from '../components/ChangePasswordModal';
import axios from 'axios';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ── neutral greys + faint sky-blue accent (+ amber for ON HOLD) ── */
const T = {
  ink: '#1f1f1f', sub: '#595959', muted: '#8c8c8c', faint: '#bfbfbf',
  border: '#d9d9d9', line: '#ececec', hairline: '#f0f0f0',
  panel: '#fafafa', head: '#f5f5f5', white: '#ffffff', appbg: '#f6f7f8',
  blue: '#3f74ad', blueHover: '#4f86c6', blueActive: '#356296',
  blueSoft: '#eaf3fc', blueLine: '#d6e6f5',
  // ON HOLD accent
  hold: '#d46b08', holdSoft: '#fff7e6', holdLine: '#ffd591',
};
const primaryBtn = { background: T.blue, borderColor: T.blue, color: '#fff' };

const GRID_CSS = `
  .pts-dash .ant-table { font-size: 12px; }
  .pts-dash .ant-table-cell { padding: 6px 12px !important; border-color: ${T.line} !important; white-space: nowrap; }
  .pts-dash .ant-table-thead > tr > th {
    background: ${T.head} !important; color: ${T.sub} !important; font-weight: 600 !important;
    border-color: ${T.line} !important; padding: 7px 12px !important;
  }
  .pts-dash .ant-table-tbody > tr:hover > td { background: ${T.blueSoft} !important; }
  .pts-dash .ant-table-row-hold > td { background: #fffdf5; }
`;

const CATEGORY_LABELS = {
  material: 'Materials', labor_supply: 'Labor Supply', sub_contractor: 'Sub-Contractors',
  heavy_equipment_rental: 'Heavy Equip.', equipment_rental: 'Equipment',
  fees_and_charges: 'Fees & Charges', repairs_and_maintenance: 'Repairs & Maint.',
};

const STATUS_LABELS = {
  pending_review: 'Pending Review', under_review: 'Under Review',
  on_hold: 'On Hold', approved: 'Approved', rejected: 'Rejected',
};

const formatAmount = (val) => {
  if (!val && val !== 0) return '-';
  return `AED ${Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDateTime = (val) => {
  if (!val) return '-';
  return new Date(val).toLocaleString('en-GB', {
    timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const StatusCell = ({ status }) => {
  const label = STATUS_LABELS[status] || status || '-';
  const rejected = status === 'rejected';
  const held = status === 'on_hold';
  const borderColor = held ? T.holdLine : (rejected ? T.blue : T.border);
  const textColor = held ? T.hold : (rejected ? T.blue : T.ink);
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 2,
      border: `1px solid ${borderColor}`,
      background: held ? T.holdSoft : 'transparent',
      fontSize: 11, color: textColor,
      fontWeight: (rejected || held) ? 600 : 500, lineHeight: '16px',
    }}>{label}</span>
  );
};

const ProcurementDashboard = ({ username, role, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);

  // How-to modal
  const [howToOpen, setHowToOpen] = useState(false);

  // Batch overview
  const [batchRows, setBatchRows] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);

  // Batch → submissions drawer
  const [batchDrawerOpen, setBatchDrawerOpen] = useState(false);
  const [drawerBatch, setDrawerBatch] = useState(null);
  const [drawerStatus, setDrawerStatus] = useState('all');
  const [activeReviseSub, setActiveReviseSub] = useState(null);

  const isAccounts = role === 'procurement_accounts' || role === 'procurement_admin';
  const canViewAll = true;
  const displayName = username.split('@')[0] || username;
  const initials = displayName.substring(0, 2).toUpperCase();

  const fetchDashboard = useCallback(async () => {
    setLoadingStats(true);
    try {
      const params = canViewAll ? {} : { submitted_by: username };
      const statsRes = await axios.get(`${API_BASE_URL}/api/procurement/dashboard/stats`, { params });
      setStats(statsRes.data.stats || {});
    } catch (err) { console.error('Dashboard fetch error:', err); }
    finally { setLoadingStats(false); }
  }, [username, isAccounts]);

  // Derive batch-wise breakdown by grouping submissions
  const fetchBatchOverview = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const params = canViewAll ? { limit: 2000, offset: 0 } : { submitted_by: username, limit: 2000, offset: 0 };
      const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions`, { params });
      const subs = res.data.submissions || [];
      const RECENT_CUTOFF = Date.now() - 48 * 60 * 60 * 1000; // last 48 hours
      const map = {};
      subs.forEach(s => {
        const b = s.batch_no;
        if (b == null) return;
        if (!map[b]) map[b] = { batch_no: b, total: 0, pending: 0, on_hold: 0, approved: 0, rejected: 0, value: 0, recent48: 0, items: [] };
        map[b].total += 1;
        map[b].value += s.total_amount || 0;
        map[b].items.push(s);
        if (s.submitted_at && new Date(s.submitted_at).getTime() >= RECENT_CUTOFF) map[b].recent48 += 1;
        if (s.status === 'approved') map[b].approved += 1;
        else if (s.status === 'rejected') map[b].rejected += 1;
        else if (s.status === 'on_hold') map[b].on_hold += 1;   // held is NOT pending
        else map[b].pending += 1;
      });
      setBatchRows(Object.values(map).sort((a, b) => b.batch_no - a.batch_no));
    } catch (err) { console.error('Batch overview error:', err); }
    finally { setLoadingBatches(false); }
  }, [username, isAccounts]);

  useEffect(() => { fetchDashboard(); fetchBatchOverview(); }, [fetchDashboard, fetchBatchOverview]);

  const handleMenuClick = ({ key }) => { setCurrentView(key); setSelectedSubmissionId(null); };
  const openSubmissionDetail = (id) => { setSelectedSubmissionId(id); setCurrentView('detail'); };

  const openBatchDrawer = (row, status = 'all') => { setDrawerBatch(row); setDrawerStatus(status); setActiveReviseSub(null); setBatchDrawerOpen(true); };
  const closeBatchDrawer = () => { setBatchDrawerOpen(false); setDrawerBatch(null); setDrawerStatus('all'); setActiveReviseSub(null); };

  const profileMenuItems = [
    { key: 'profile-header', label: <div style={{ padding: '8px 0' }}><Text strong style={{ display: 'block', fontSize: 14 }}>{displayName}</Text><Text type="secondary" style={{ fontSize: 12 }}>{username}</Text><div style={{ marginTop: 6, fontSize: 11, color: T.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{role.replace('_', ' ')}</div></div>, disabled: true },
    { type: 'divider' },
    { key: 'change-password', icon: <LockOutlined />, label: 'Change Password' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true },
  ];

  const handleProfileMenu = ({ key }) => {
    if (key === 'logout') onLogout();
    if (key === 'change-password') setChangePasswordVisible(true);
  };

  const navItems = [
    { key: 'dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: 'submit', icon: <FileAddOutlined />, label: 'Submit Invoice' },
    { key: 'submissions', icon: <UnorderedListOutlined />, label: isAccounts ? 'All Submissions' : 'My Submissions' },
    { key: 'logsheet', icon: <BookOutlined />, label: 'Log Sheet' },
    ...(isAccounts ? [
      { key: 'review', icon: <AuditOutlined />, label: 'Pending Review' },
      { key: 'batches', icon: <NumberOutlined />, label: 'Batch Management' },
    ] : []),
  ];

  /* ── Batch overview table ── */
  const countCell = (v, row, status, opts = {}) => {
    const { emphasis, hold } = opts;
    const color = hold ? T.hold : T.blue;
    return v > 0
      ? <span style={{ fontSize: 12, fontWeight: (emphasis || hold) ? 600 : 500, color, textDecoration: 'underline', cursor: 'pointer' }}
          onClick={() => openBatchDrawer(row, status)}>{emphasis ? `${v} · Revise` : v}</span>
      : <span style={{ fontSize: 12, color: T.faint }}>0</span>;
  };

  const batchColumns = [
    { title: 'Batch', dataIndex: 'batch_no', key: 'batch', width: 90, render: (v, row) => <span style={{ fontSize: 12, fontWeight: 600, color: T.blue, textDecoration: 'underline', cursor: 'pointer' }} onClick={() => openBatchDrawer(row, 'all')}>Batch {v}</span> },
    { title: 'Submitted', dataIndex: 'total', key: 'total', width: 100, align: 'center', render: (v, row) => countCell(v, row, 'all') },
    { title: 'Pending', dataIndex: 'pending', key: 'pending', width: 100, align: 'center', render: (v, row) => countCell(v, row, 'pending') },
    { title: 'On Hold', dataIndex: 'on_hold', key: 'on_hold', width: 100, align: 'center', render: (v, row) => countCell(v, row, 'hold', { hold: true }) },
    { title: 'Approved', dataIndex: 'approved', key: 'approved', width: 100, align: 'center', render: (v, row) => countCell(v, row, 'approved') },
    { title: 'Rejected', dataIndex: 'rejected', key: 'rejected', width: 110, align: 'center', render: (v, row) => countCell(v, row, 'rejected', { emphasis: true }) },
    {
      title: 'Recently Uploaded', key: 'recent',
      render: (_, row) => row.recent48 > 0 ? (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 12, fontWeight: 600, color: T.blue,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.blue }} />
          {row.recent48} uploaded in 48h
        </span>
      ) : null,
    },
    { title: 'Batch Value', dataIndex: 'value', key: 'value', width: 200, align: 'right', render: (v) => <Text strong style={{ fontSize: 12, color: T.ink }}>{formatAmount(v)}</Text> },
  ];

  const Stat = ({ label, value, color }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <Text strong style={{ color: color || T.ink, fontSize: 15 }}>{value}</Text>
      <Text style={{ fontSize: 12, color: T.sub }}>{label}</Text>
    </div>
  );

  const totalOnHold = batchRows.reduce((s, r) => s + (r.on_hold || 0), 0);

  const renderContent = () => {
    if (currentView === 'submit') return <InvoiceSubmissionForm username={username} onSuccess={() => { fetchDashboard(); fetchBatchOverview(); setCurrentView('dashboard'); }} />;
    if (currentView === 'submissions') return <SubmissionsList username={username} isAccounts={isAccounts} viewAll={canViewAll} onViewDetail={openSubmissionDetail} statusFilter={null} />;
    if (currentView === 'logsheet') return <LogSheetView username={username} isAccounts={isAccounts} viewAll={canViewAll} onViewDetail={openSubmissionDetail} />;
    if (currentView === 'review') return <SubmissionsList username={username} isAccounts={isAccounts} viewAll={canViewAll} onViewDetail={openSubmissionDetail} statusFilter="pending_review" />;
    if (currentView === 'batches') return <BatchManagementView username={username} onViewSubmission={openSubmissionDetail} />;
    if (currentView === 'detail' && selectedSubmissionId) return <SubmissionDetail submissionId={selectedSubmissionId} isAccounts={isAccounts} username={username} onBack={() => setCurrentView(isAccounts ? 'submissions' : 'logsheet')} onReviewComplete={() => { fetchDashboard(); fetchBatchOverview(); }} />;

    // Dashboard
    return (
      <div className="pts-dash">
        <style>{GRID_CSS}</style>

        {/* Stat strip */}
        <div style={{ background: T.white, borderRadius: 4, border: `1px solid ${T.line}`, padding: '14px 22px', marginBottom: 20, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 22 }}>
          {loadingStats ? <Spin size="small" /> : (<>
            <Stat label="total" value={stats?.total || 0} />
            <Divider type="vertical" style={{ height: 18, margin: 0 }} />
            <Stat label="pending" value={stats?.pending || 0} />
            <Divider type="vertical" style={{ height: 18, margin: 0 }} />
            <Stat label="on hold" value={stats?.on_hold || 0} color={(stats?.on_hold || 0) > 0 ? T.hold : T.ink} />
            <Divider type="vertical" style={{ height: 18, margin: 0 }} />
            <Stat label="approved" value={stats?.approved || 0} />
            <Divider type="vertical" style={{ height: 18, margin: 0 }} />
            <Stat label="rejected" value={stats?.rejected || 0} />
            <Divider type="vertical" style={{ height: 18, margin: 0 }} />
            <Text style={{ fontSize: 13, color: T.ink, fontWeight: 600 }}>Total Value: {formatAmount(stats?.total_value)}</Text>
          </>)}
        </div>

        {/* ── ON HOLD notice (users only — accounts owns the hold, they don't need chasing) ── */}
        {!isAccounts && totalOnHold > 0 && (
          <Alert
            type="warning" showIcon icon={<PauseCircleOutlined />}
            message={`${totalOnHold} invoice${totalOnHold > 1 ? 's are' : ' is'} on hold with Accounts`}
            description="Accounts has parked these while they review them. Nothing to re-upload — open the log sheet to read the note against each one."
            action={<Button size="small" onClick={() => setCurrentView('logsheet')} style={{ borderRadius: 2 }}>Open log sheet</Button>}
            style={{ marginBottom: 20, borderRadius: 4 }}
          />
        )}

        {/* Quick actions */}
        <div style={{ background: T.white, borderRadius: 4, border: `1px solid ${T.line}`, padding: '18px 22px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <Title level={5} style={{ margin: 0, color: T.ink }}>SUBMIT</Title>
            <Text style={{ color: T.muted, fontSize: 13 }}>Submit new invoices or open the log sheet</Text>
          </div>
          <Space>
            <Button type="primary" size="large" onClick={() => setCurrentView('submit')} style={{ ...primaryBtn, borderRadius: 4, height: 42, fontWeight: 600 }}>Submit new invoice</Button>
            <Button size="large" onClick={() => setCurrentView('logsheet')} style={{ borderRadius: 4, height: 42 }}>Log sheet</Button>
            {isAccounts && <Button size="large" onClick={() => setCurrentView('batches')} style={{ borderRadius: 4, height: 42 }}>Batch management</Button>}
          </Space>
        </div>

        {/* Batch-wise breakdown */}
        <div style={{ background: T.white, borderRadius: 4, border: `1px solid ${T.line}` }}>
          <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.hairline}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text strong style={{ fontSize: 14, color: T.ink }}>Batch-wise breakdown</Text>
            <Text style={{ fontSize: 12, color: T.muted }}>Click any count to view those submissions · rejected counts open the revise flow</Text>
          </div>
          {loadingBatches ? <div style={{ padding: 50, textAlign: 'center' }}><Spin /></div>
            : batchRows.length === 0 ? <Empty style={{ padding: 40 }} description={<Text type="secondary">No batches yet</Text>} />
              : <Table columns={batchColumns} dataSource={batchRows.map(r => ({ ...r, key: r.batch_no }))}
                  rowClassName={(r) => r.on_hold > 0 ? 'ant-table-row-hold' : ''}
                  pagination={batchRows.length > 12 ? { pageSize: 12, size: 'small', showSizeChanger: false } : false} size="small" bordered />}
        </div>
      </div>
    );
  };

  const STATUS_FILTERS = {
    all: () => true,
    pending: (s) => !['approved', 'rejected', 'on_hold'].includes(s.status),
    hold: (s) => s.status === 'on_hold',
    approved: (s) => s.status === 'approved',
    rejected: (s) => s.status === 'rejected',
  };
  const DRAWER_TITLES = {
    all: 'All submissions', pending: 'Pending', hold: 'On hold with accounts',
    approved: 'Approved', rejected: 'Rejected invoices',
  };
  const drawerItems = drawerBatch ? (drawerBatch.items || []).filter(STATUS_FILTERS[drawerStatus] || (() => true)) : [];

  const viewLabels = { dashboard: 'Dashboard', submit: 'Submit Invoice', submissions: 'Submissions', review: 'Pending Review', detail: 'Submission Detail', batches: 'Batch Management', logsheet: 'Log Sheet' };

  return (
    <Layout className="pts-dash" style={{ minHeight: '100vh' }}>
      <style>{GRID_CSS}</style>

      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} trigger={null} width={252} collapsedWidth={68}
        style={{ background: T.white, borderRight: `1px solid ${T.line}`, position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 10, overflow: 'auto' }}>
        <div style={{ padding: collapsed ? '20px 0' : '22px 22px 18px', borderBottom: `1px solid ${T.line}`, textAlign: collapsed ? 'center' : 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: collapsed ? 'center' : 'flex-start' }}>
            <img src="/tab.png" alt="PTS Logo" style={{ width: collapsed ? 34 : 40, height: collapsed ? 34 : 40, objectFit: 'contain', flexShrink: 0 }} />
            {!collapsed && <div style={{ lineHeight: 1.25 }}><div style={{ fontSize: 14, fontWeight: 800, color: T.ink, letterSpacing: 0.3 }}>PHOENICIAN</div><div style={{ fontSize: 9.5, color: T.muted, letterSpacing: 1.8, fontWeight: 600 }}>PROCUREMENT</div></div>}
          </div>
        </div>
        <div style={{ marginTop: 14, padding: collapsed ? '0 10px' : '0 14px' }}>
          {!collapsed && <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.4, color: T.muted, padding: '0 10px 10px' }}>PROCUREMENT</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {navItems.map((it) => {
              const active = currentView === it.key;
              const abbr = it.label.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
              const node = (
                <div
                  onClick={() => handleMenuClick({ key: it.key })}
                  style={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    height: 38, padding: collapsed ? 0 : '0 14px', borderRadius: 6, cursor: 'pointer',
                    background: active ? T.blueSoft : 'transparent',
                    boxShadow: active && !collapsed ? `inset 3px 0 0 ${T.blue}` : 'none',
                    transition: 'background .15s',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = T.panel; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{
                    whiteSpace: 'nowrap',
                    fontSize: collapsed ? 12 : 14,
                    fontWeight: 700,
                    color: T.ink,
                  }}>
                    {collapsed ? abbr : it.label}
                  </span>
                </div>
              );
              return collapsed
                ? <Tooltip key={it.key} title={it.label} placement="right">{node}</Tooltip>
                : <div key={it.key}>{node}</div>;
            })}
          </div>
        </div>
        {!collapsed && <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, background: T.panel, borderRadius: 4, padding: '12px 14px', border: `1px solid ${T.line}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar size={36} style={{ background: T.blue, fontSize: 13, fontWeight: 700 }}>{initials}</Avatar>
            <div style={{ overflow: 'hidden', flex: 1 }}><Text style={{ color: T.ink, fontSize: 13, fontWeight: 600, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</Text><Text style={{ fontSize: 10.5, color: T.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{role.replace('_', ' ')}</Text></div>
          </div>
        </div>}
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 68 : 252, transition: 'margin-left 0.2s', background: T.appbg }}>
        <Header style={{ background: T.white, padding: '0 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${T.line}`, position: 'sticky', top: 0, zIndex: 9, height: 58 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed(!collapsed)} style={{ fontSize: 16, width: 36, height: 36, color: T.sub }} />
            <Breadcrumb items={[{ title: <span style={{ color: T.muted }}>Procurement</span> }, { title: <span style={{ color: T.ink, fontWeight: 500 }}>{viewLabels[currentView] || 'Dashboard'}</span> }]} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button onClick={() => setHowToOpen(true)} style={{ borderRadius: 4 }}>How to use</Button>
            <Dropdown menu={{ items: profileMenuItems, onClick: handleProfileMenu }} trigger={['click']} placement="bottomRight">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '5px 10px 5px 5px', borderRadius: 4, border: '1px solid transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.panel; e.currentTarget.style.borderColor = T.line; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}>
                <Avatar size={34} style={{ background: T.blue, fontWeight: 700, fontSize: 13 }}>{initials}</Avatar>
                <div style={{ lineHeight: 1.25 }}><Text strong style={{ fontSize: 13, display: 'block', color: T.ink }}>{displayName}</Text><Text style={{ fontSize: 11, color: T.muted }}>{role.replace('_', ' ')}</Text></div>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{ padding: 22 }}>
          {/* Plain header band — no gradient, no colour */}
          <div style={{ background: T.white, border: `1px solid ${T.line}`, borderRadius: 4, padding: '18px 24px', marginBottom: 20 }}>
            <Title level={4} style={{ color: T.ink, margin: 0, fontWeight: 700, fontSize: 18 }}>Procurement — Invoice Submission</Title>
            <Text style={{ color: T.muted, fontSize: 13 }}>Submit, track, and manage procurement invoices with batch tracking</Text>
          </div>
          {renderContent()}
        </Content>

        <div style={{ textAlign: 'center', padding: '16px 22px 20px', color: T.muted, fontSize: 12 }}>2026 Phoenician Technical Services LLC</div>
      </Layout>

      {/* ── Batch submissions drawer ── */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: T.ink }}>
              {activeReviseSub ? 'Submit Revised Invoice' : `${DRAWER_TITLES[drawerStatus] || 'Submissions'} — Batch ${drawerBatch?.batch_no}`}
            </span>
            {!activeReviseSub && <span style={{ fontSize: 12, color: T.sub, border: `1px solid ${T.border}`, borderRadius: 2, padding: '1px 8px' }}>{drawerItems.length}</span>}
            {activeReviseSub && <span style={{ fontSize: 12, color: T.sub, border: `1px solid ${T.border}`, borderRadius: 2, padding: '1px 8px' }}>{activeReviseSub.submission_ref}</span>}
          </div>
        }
        placement="right" open={batchDrawerOpen} onClose={closeBatchDrawer}
        width={Math.min(680, window.innerWidth * 0.9)} destroyOnClose
      >
        {!activeReviseSub ? (
          <div>
            {drawerStatus === 'rejected' && (
              <Alert
                type="warning" showIcon
                message="Re-submit under the same batch"
                description={`These invoices were rejected in Batch ${drawerBatch?.batch_no}. Upload each corrected document through Revise so it stays in Batch ${drawerBatch?.batch_no} — do not re-submit a rejected invoice as a new entry in a different batch.`}
                style={{ marginBottom: 16, borderRadius: 4 }}
              />
            )}
            {drawerStatus === 'hold' && (
              <Alert
                type="warning" showIcon icon={<PauseCircleOutlined />}
                message="On hold with Accounts"
                description="Accounts has parked these invoices while they review them. There is nothing to re-upload or revise — the note under each one names the supplier and invoice number."
                style={{ marginBottom: 16, borderRadius: 4 }}
              />
            )}
            {drawerItems.map((s) => {
              const held = s.status === 'on_hold';
              return (
                <div key={s.id} style={{
                  border: `1px solid ${held ? T.holdLine : T.line}`, borderRadius: 4,
                  padding: '12px 14px', marginBottom: 10,
                  background: held ? '#fffdf5' : T.white,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <Text strong style={{ fontSize: 13, color: T.ink }}>{s.vendor_name || '-'}</Text>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                        LPO {s.lpo_number || '-'} · {CATEGORY_LABELS[s.category] || s.category} · {formatAmount(s.total_amount)}
                      </div>
                    </div>
                    <Space size={10}>
                      <StatusCell status={s.status} />
                      <span style={{ fontSize: 12, color: T.blue, textDecoration: 'underline', cursor: 'pointer' }} onClick={() => { closeBatchDrawer(); openSubmissionDetail(s.id); }}>View</span>
                      {s.status === 'rejected' && !isAccounts && <Button size="small" style={{ ...primaryBtn, borderRadius: 2 }} onClick={() => setActiveReviseSub(s)}>Revise</Button>}
                    </Space>
                  </div>
                  {held ? (
                    <div style={{ marginTop: 8, padding: '6px 10px', background: T.holdSoft, border: `1px solid ${T.holdLine}`, borderRadius: 4 }}>
                      <Text style={{ fontSize: 11, color: T.hold, fontWeight: 600, display: 'block' }}>
                        {s.hold_remarks || s.accounts_remarks}
                      </Text>
                      {s.held_at && (
                        <Text style={{ fontSize: 10, color: T.muted }}>
                          Held by {s.held_by ? s.held_by.split('@')[0] : 'accounts'} on {formatDateTime(s.held_at)}
                        </Text>
                      )}
                    </div>
                  ) : s.accounts_remarks ? (
                    <div style={{ marginTop: 8, padding: '6px 10px', background: T.panel, border: `1px solid ${T.line}`, borderRadius: 4 }}>
                      <Text style={{ fontSize: 11, color: T.sub }}>{s.accounts_remarks}</Text>
                    </div>
                  ) : null}
                </div>
              );
            })}
            {drawerItems.length === 0 && <Empty description="No submissions" />}
          </div>
        ) : (
          <div>
            <Button size="small" onClick={() => setActiveReviseSub(null)} style={{ borderRadius: 2, marginBottom: 14 }}>Back to list</Button>
            {activeReviseSub.accounts_remarks && (
              <Alert type="error" showIcon message="Rejection reason" description={activeReviseSub.accounts_remarks} style={{ marginBottom: 16, borderRadius: 4 }} />
            )}
            <div style={{ marginBottom: 16, borderRadius: 4, border: `1px solid ${T.line}`, padding: '12px 14px', background: T.panel }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Batch</Text><Text strong>{activeReviseSub.batch_no}</Text></div>
                <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>LPO</Text><Text strong>{activeReviseSub.lpo_number}</Text></div>
                <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Vendor</Text><Text>{activeReviseSub.vendor_name || '-'}</Text></div>
                <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Amount</Text><Text strong>{formatAmount(activeReviseSub.total_amount)}</Text></div>
              </div>
            </div>
            <RevisionUploadForm
              submissionId={activeReviseSub.id}
              submissionRef={activeReviseSub.submission_ref}
              category={activeReviseSub.category}
              rejectionRemarks={activeReviseSub.accounts_remarks}
              username={username}
              onComplete={() => { closeBatchDrawer(); fetchDashboard(); fetchBatchOverview(); }}
            />
          </div>
        )}
      </Drawer>

      {/* ── Rejected-invoice reminder (login + every 30 min, users only) ──
          Deliberately NOT extended to held invoices: a hold means accounts is
          working on it, so the user has nothing to fix and should not be nagged. */}
      <RejectedInvoicesReminder
        username={username}
        isAccounts={isAccounts}
        onRevise={() => setCurrentView('logsheet')}
      />

      <ProcurementHowToModal username={username} forceOpen={howToOpen} onClose={() => setHowToOpen(false)} />
      <ChangePasswordModal open={changePasswordVisible} onClose={() => setChangePasswordVisible(false)} username={username} />
    </Layout>
  );
};

export default ProcurementDashboard;